import React from 'react';
/**
 * Types for remote module loading and configuration
 */
interface RemoteModuleConfig {
  retryAttempts?: number;
  retryDelay?: number;
  timeout?: number;
}

interface RemoteContainer {
  get: (module: string) => Promise<() => any>;
  init: (shareScope: unknown) => void;
}

type RemoteComponent<T = any> = React.ComponentType<T>;

type ModuleFactory = () => Promise<{ default: any }>;
/**
 * Default configuration for remote module loading
 */
const DEFAULT_CONFIG: Required<RemoteModuleConfig> = {
  retryAttempts: 3,
  retryDelay: 1000,
  timeout: 10000,
} as const;

/**
 * Loads a remote module with retry and timeout capabilities
 *
 * @param moduleName - Name of the remote module to load
 * @param config - Optional configuration for loading behavior
 * @returns Promise resolving to the remote component
 *
 * @example
 * ```typescript
 * const MyComponent = React.lazy(() =>
 *   loadRemoteModule('MyComponent', {
 *     retryAttempts: 3,
 *     timeout: 3000
 *   })
 * );
 * ```
 */
export async function loadRemoteModule<T = any>(
  moduleName: string,
  config: RemoteModuleConfig = {},
): Promise<{ default: RemoteComponent<T> }> {
  // Merge provided config with defaults
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  /**
   * Attempts to load the remote module
   * @throws {Error} If module loading fails
   */
  const attemptLoad = async (): Promise<{ default: RemoteComponent<T> }> => {
    const container = (window as { remote?: RemoteContainer }).remote;

    if (!container) {
      throw new Error('Remote container not initialized. ' + 'Ensure Module Federation is properly configured.');
    }

    try {
      // Add timeout to module loading
      const modulePromise = container.get(`./${moduleName}`);
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error(`Loading ${moduleName} timed out after ${finalConfig.timeout}ms`));
        }, finalConfig.timeout);
      });

      // Race between module loading and timeout
      const factory = (await Promise.race([modulePromise, timeoutPromise])) as ModuleFactory;
      const Module = await factory();

      return {
        default: Module.default || Module,
      };
    } catch (error) {
      console.error(`Failed to load module ${moduleName}:`, error);
      throw error;
    }
  };

  /**
   * Implements retry logic for module loading
   */
  let lastError: Error | undefined;

  for (let attempt = 1; attempt <= finalConfig.retryAttempts; attempt++) {
    try {
      return await attemptLoad();
    } catch (error) {
      lastError = error as Error;

      // Log retry attempts in local
      if (process.env.NODE_ENV === 'local') {
        console.warn(`Attempt ${attempt}/${finalConfig.retryAttempts} to load ${moduleName} failed:`, error);
      }

      // If this was the last attempt, throw the error
      if (attempt === finalConfig.retryAttempts) {
        throw new Error(
          `Failed to load ${moduleName} after ${finalConfig.retryAttempts} attempts. ` +
            `Last error: ${lastError?.message}`,
        );
      }

      // Wait before next retry
      await new Promise((resolve) => setTimeout(resolve, finalConfig.retryDelay));
    }
  }

  // This should never be reached due to the throw above, but TypeScript needs it
  throw lastError;
}

/**
 * HOC to wrap components with remote module loading error handling
 */
export function withRemoteLoading<T = any>(moduleName: string, config?: RemoteModuleConfig) {
  return React.lazy(() => loadRemoteModule<T>(moduleName, config));
}

// Usage examples:
/*
 // Basic usage
 const RemoteComponent = React.lazy(() => loadRemoteModule('ComponentName'));
 
 // With configuration
 const ConfiguredComponent = React.lazy(() => 
  loadRemoteModule('ComponentName', {
    retryAttempts: 5,
    retryDelay: 2000,
    timeout: 10000
  })
 );
 
 // Using HOC
 const RemoteComponent = withRemoteLoading('ComponentName');
 
 // With TypeScript props
 interface MyComponentProps {
  name: string;
  age: number;
 }
 
 const TypedComponent = withRemoteLoading<MyComponentProps>('ComponentName');
 */
